/**
 * [INPUT]: 依赖 react
 * [OUTPUT]: 对外提供 Uploader 组件（选图/拍照/粘贴 + Shimmer 扫描 Reveal 中态）
 * [POS]: apps/web/components 的上传入口，调用 App 的 onFile 回调；对齐 docs §8
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../../CLAUDE.md
 */
import { useRef, useState, useEffect } from 'react'
import { useModelStatus } from '../hooks/useModelStatus'
import { trackEvent } from '../analytics'

interface Props {
  onFile: (file: File) => void
  loading: boolean
}

function getFriendlyCameraError(err: any): string {
  if (err instanceof DOMException || err instanceof Error) {
    const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

    switch (err.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return isMobile
          ? 'Camera access denied. Please go to your phone Settings -> Privacy -> Camera (or Browser Permissions) to allow access.'
          : 'Camera access denied. Please click the camera icon in your browser address bar or check system privacy settings to allow access.'
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return isMobile
          ? 'No camera detected. If you are using an in-app browser (like WeChat, Line, or Instagram), please open this page in your system browser (Safari/Chrome).'
          : 'No camera detected. If you are using a laptop with the lid closed, please open it, or verify your camera is connected.'
      case 'NotReadableError':
      case 'TrackStartError':
        return isMobile
          ? 'Camera is already in use. Please close background video calls or other camera apps and try again.'
          : 'Camera is already in use. Please close any other apps (like Zoom, Teams, WeChat) using your webcam and try again.'
      case 'OverconstrainedError':
        return 'Camera resolution settings are not supported. Please try uploading a photo instead.'
    }
    return `${err.name}: ${err.message}`
  }
  return String(err)
}

export function Uploader({ onFile, loading }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const modelStatus = useModelStatus()
  const [dragOver, setDragOver] = useState(false)

  // Camera capture states
  const [cameraActive, setCameraActive] = useState(false)
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null)
  const [cameraLoading, setCameraLoading] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)

  // Countdown states
  const [countdown, setCountdown] = useState<number | null>(null)
  const countdownIntervalRef = useRef<number | null>(null)

  function pick(file: File | undefined) {
    if (file) onFile(file)
  }

  // Handle clipboard paste
  useEffect(() => {
    function handlePaste(e: ClipboardEvent) {
      if (loading || cameraActive) return
      const item = e.clipboardData?.items[0]
      if (item && item.type.indexOf('image') === 0) {
        const file = item.getAsFile()
        if (file) {
          trackEvent('Image Pasted')
          pick(file)
        }
      }
    }

    window.addEventListener('paste', handlePaste)
    return () => {
      window.removeEventListener('paste', handlePaste)
    }
  }, [loading, cameraActive])

  // Camera access lifecycle
  async function startCamera() {
    trackEvent('Take Photo Click')
    const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

    // Fallback on environments without WebRTC getUserMedia (insecure HTTP context or unsupported webviews)
    if (!navigator?.mediaDevices?.getUserMedia) {
      if (isMobile && cameraInputRef.current) {
        cameraInputRef.current.click()
        return
      }
      trackEvent('Camera Error', { error_name: 'InsecureContextOrUnsupported' })
      setCameraActive(true)
      setCameraLoading(false)
      setCameraError('Camera access requires a secure context (HTTPS or localhost), or is not supported by this browser.')
      return
    }

    setCameraLoading(true)
    setCameraError(null)
    setCameraActive(true)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'user' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      })
      setCameraStream(stream)
    } catch (err: any) {
      console.error('Camera access error:', err)
      trackEvent('Camera Error', { error_name: err?.name || 'Unknown' })
      // If mobile browser fails WebRTC, attempt native camera input fallback
      if (isMobile && cameraInputRef.current) {
        stopCamera()
        cameraInputRef.current.click()
        return
      }
      setCameraError(getFriendlyCameraError(err))
    } finally {
      setCameraLoading(false)
    }
  }

  function stopCamera() {
    if (countdownIntervalRef.current) {
      window.clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }
    setCountdown(null)

    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop())
    }
    setCameraStream(null)
    setCameraActive(false)
    setCameraError(null)
  }

  function startCountdown() {
    if (countdown !== null) return
    if (countdownIntervalRef.current) {
      window.clearInterval(countdownIntervalRef.current)
    }

    let count = 3
    setCountdown(count)

    countdownIntervalRef.current = window.setInterval(() => {
      count -= 1
      if (count <= 0) {
        if (countdownIntervalRef.current) {
          window.clearInterval(countdownIntervalRef.current)
          countdownIntervalRef.current = null
        }
        setCountdown(null)
        capturePhoto()
      } else {
        setCountdown(count)
      }
    }, 1000)
  }

  function capturePhoto() {
    if (!videoRef.current) return
    const video = videoRef.current
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth || 640
    canvas.height = video.videoHeight || 480
    const ctx = canvas.getContext('2d')
    if (ctx) {
      // Mirror the image to match the preview
      ctx.translate(canvas.width, 0)
      ctx.scale(-1, 1)
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      
      canvas.toBlob((blob) => {
        if (blob) {
          trackEvent('Camera Photo Captured')
          const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' })
          pick(file)
          stopCamera()
        }
      }, 'image/jpeg', 0.95)
    }
  }

  // Bind camera stream to video element when ready
  useEffect(() => {
    if (videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream
      videoRef.current.play().catch(() => {})
    }
  }, [cameraStream, cameraActive])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        window.clearInterval(countdownIntervalRef.current)
      }
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop())
      }
    }
  }, [cameraStream])

  if (cameraActive) {
    return (
      <div className="max-w-md w-full space-y-6">
        <div className="relative w-full rounded-3xl border border-slate-800 bg-slate-950 overflow-hidden aspect-[4/3] flex flex-col items-center justify-center shadow-inner">
          {cameraLoading ? (
            <div className="space-y-3 flex flex-col items-center">
              <div className="inline-block w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
              <div className="text-xs text-slate-400">Starting camera…</div>
            </div>
          ) : cameraError ? (
            <div className="p-6 text-center space-y-4">
              <div className="text-sm text-terracotta font-medium">{cameraError}</div>
              <button
                onClick={stopCamera}
                className="rounded-xl border border-slate-800 hover:border-slate-700 bg-slate-900 text-xs text-slate-300 px-4 py-2 transition"
              >
                Go Back
              </button>
            </div>
          ) : (
            <>
              {/* Mirrored Video Preview */}
              <video
                ref={(el) => {
                  videoRef.current = el
                  if (el && cameraStream && el.srcObject !== cameraStream) {
                    el.srcObject = cameraStream
                    el.play().catch(() => {})
                  }
                }}
                playsInline
                autoPlay
                muted
                className="absolute inset-0 w-full h-full object-cover scale-x-[-1] rounded-3xl"
              />

              {/* Face Guide Overlay (Golden dashed oval acting as a centering grid) */}
              <div className="absolute inset-0 pointer-events-none z-10">
                <div className="absolute top-[6%] left-[22.5%] w-[55%] h-[66%] border-[2px] border-dashed border-brass/30 rounded-[50%]">
                  {/* Alignment aids */}
                  <div className="absolute top-[25%] left-1/2 -translate-x-1/2 w-4 h-px bg-brass/20" />
                  <div className="absolute top-[48%] left-1/2 -translate-x-1/2 w-3.5 h-3.5 border border-brass/15 rounded-full" />
                  <div className="absolute bottom-[25%] left-1/2 -translate-x-1/2 w-8 h-px bg-brass/20" />
                </div>
              </div>

              {/* Top Guideline Instruction */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-slate-950/75 border border-brass/25 backdrop-blur-md shadow-lg pointer-events-none z-20">
                <div className="text-[10px] text-brass font-semibold tracking-widest uppercase text-center">
                  Center face & look straight
                </div>
              </div>

              {/* Countdown Overlay */}
              {countdown !== null && (
                <div className="absolute inset-0 bg-slate-950/30 backdrop-blur-[1px] flex items-center justify-center z-30 pointer-events-none">
                  <div key={countdown} className="text-8xl font-bold text-[#FFEAA7] font-mono drop-shadow-[0_8px_24px_rgba(0,0,0,0.6)] animate-pulse">
                    {countdown}
                  </div>
                </div>
              )}

              {/* Bottom Control Bar */}
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-slate-950/95 via-slate-950/80 to-transparent flex items-center justify-between px-6 z-20">
                <button
                  type="button"
                  onClick={stopCamera}
                  className="rounded-full bg-slate-900/80 hover:bg-slate-800 border border-slate-800 text-xs text-slate-300 py-1.5 px-4 transition active:scale-95"
                >
                  Cancel
                </button>
                
                <div className="absolute left-1/2 -translate-x-1/2">
                  <button
                    type="button"
                    disabled={countdown !== null}
                    onClick={startCountdown}
                    className={`w-14 h-14 rounded-full border-[3px] border-white/95 flex items-center justify-center bg-transparent transition shadow-xl group ${
                      countdown !== null ? 'opacity-50 cursor-not-allowed scale-90' : 'hover:border-white active:scale-95 hover:scale-105'
                    }`}
                    title="Capture Photo"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#FFEAA7] to-[#E8A34D] group-hover:scale-105 transition-transform duration-100 flex items-center justify-center">
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-950/30 animate-pulse" />
                    </div>
                  </button>
                </div>

                {/* Spacer to balance layout */}
                <div className="w-14 h-5 opacity-0 pointer-events-none" />
              </div>
            </>
          )}
        </div>

        {/* Local Processing Privacy Hint */}
        <div className="pt-4 border-t border-slate-900">
          <p className="text-center text-[11px] text-slate-500 leading-relaxed">
            🔒 Video feed is processed entirely locally — no frames or video data are ever uploaded.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-md w-full space-y-6">
      {/* Upload Box (centered dotted/dashed card) */}
      <div
        onDragOver={(e) => {
          if (loading) return
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (loading) return
          e.preventDefault()
          setDragOver(false)
          const file = e.dataTransfer.files?.[0]
          if (file) {
            trackEvent('File Dropped')
            pick(file)
          }
        }}
        className={
          'w-full rounded-3xl border-2 border-dashed py-14 px-6 text-center transition-all duration-300 ' +
          (dragOver
            ? 'border-brass bg-brass/5 shadow-[0_0_30px_rgba(197,170,115,0.06)]'
            : 'border-slate-800 bg-slate-950/20') +
          (loading ? ' opacity-60 cursor-wait' : ' cursor-default')
        }
      >
        {loading ? (
          modelStatus === 'ready' ? (
            // Stage 2 — model warm: actual geometry scan animation
            <div className="space-y-5 py-1 flex flex-col items-center justify-center">
              <div className="photo-scan-reveal relative h-40 w-full max-w-[260px] overflow-hidden rounded-2xl border border-brass/25 bg-slate-950 shadow-[0_0_35px_rgba(197,170,115,0.08)]">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(197,170,115,0.15),transparent_30%),linear-gradient(135deg,rgba(15,23,42,0.96),rgba(15,23,42,0.66))]" />
                <div className="absolute left-1/2 top-8 h-14 w-14 -translate-x-1/2 rounded-full border border-slate-500/35 bg-slate-800/70" />
                <div className="absolute left-1/2 top-[88px] h-16 w-28 -translate-x-1/2 rounded-t-[44px] border border-slate-500/30 bg-slate-800/55" />
                <div className="absolute left-8 right-8 top-1/2 h-px bg-brass/35" />
                <div className="absolute bottom-4 left-5 right-5 grid grid-cols-3 gap-2">
                  <span className="h-1 rounded-full bg-slate-600/50" />
                  <span className="h-1 rounded-full bg-slate-600/35" />
                  <span className="h-1 rounded-full bg-slate-600/45" />
                </div>
                <div className="photo-scan-grid absolute inset-0 opacity-50" />
                <div className="photo-scan-line absolute left-0 right-0 h-12" />
                <div className="photo-scan-mask absolute inset-0 bg-slate-950/80" />
              </div>
              <div className="space-y-1">
                <div className="text-sm font-semibold text-slate-200">Analyzing facial geometry...</div>
                <div className="text-xs text-slate-500">~3s to result · Image never leaves your device</div>
              </div>
            </div>
          ) : (
            // Stage 1 — first-time engine warm-up (WASM + model download)
            <div className="space-y-5 py-1 flex flex-col items-center justify-center">
              <div className="relative h-40 w-full max-w-[260px] overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 flex items-center justify-center">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(197,170,115,0.08),transparent_60%)]" />
                <div className="relative flex flex-col items-center gap-4">
                  <div className="relative w-12 h-12">
                    <div className="absolute inset-0 rounded-full border-2 border-slate-700" />
                    <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-brass animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center text-brass">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m4.5 2.25L5.25 8.25m3 2.25l2.25-2.25M15.75 3v1.5M19.5 8.25H21m-4.5 2.25l2.25-2.25M12 21a4.5 4.5 0 004.5-4.5V12a4.5 4.5 0 10-9 0v4.5A4.5 4.5 0 0012 21z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-1 text-center">
                <div className="text-sm font-semibold text-slate-200">
                  {modelStatus === 'error' ? 'Retrying engine…' : 'Loading analysis engine…'}
                </div>
                <div className="text-xs text-slate-500 leading-relaxed max-w-[240px]">
                  One-time setup · a few seconds. Your photo never leaves your device.
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="space-y-5 flex flex-col items-center justify-center">
            {/* Upload Icon */}
            <div className="w-12 h-12 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center text-brass">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            </div>
            
            <div className="space-y-2">
              <div className="font-serif text-2xl font-medium text-slate-100 tracking-tight">
                Analyze your face
              </div>
              <div className="text-xs text-slate-400 max-w-[240px] mx-auto leading-relaxed">
                Provide a front-facing photo with neutral expression and even lighting
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-[280px] pt-1 relative z-10">
              <button
                type="button"
                onClick={() => {
                  trackEvent('Upload File Click')
                  fileInputRef.current?.click()
                }}
                className="flex-1 rounded-xl bg-slate-900 hover:bg-slate-800 text-xs text-slate-200 py-2.5 font-medium border border-slate-800 hover:border-slate-700 transition active:scale-95"
              >
                Upload File
              </button>
              
              <button
                type="button"
                onClick={startCamera}
                className="flex-1 rounded-xl bg-gradient-to-tr from-[#FFEAA7] to-[#E8A34D] hover:brightness-110 text-xs text-slate-950 py-2.5 font-semibold transition active:scale-95 flex items-center justify-center gap-1.5 shadow-md"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                  <path fillRule="evenodd" d="M1 8a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 018.07 3h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0016.07 6H17a2 2 0 012 2v7a2 2 0 01-2 2H3a2 2 0 01-2-2V8zm13.5 3a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM10 14a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                Take Photo
              </button>
            </div>

            <div className="text-[10px] text-slate-600 pt-2 border-t border-slate-900/60 w-full max-w-[200px] mx-auto">
              Drag & drop here or paste (Cmd/Ctrl+V)
            </div>
          </div>
        )}
      </div>

      {/* File Upload Input (Native file picker / photo library) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          pick(e.target.files?.[0])
          e.target.value = ''
        }}
      />

      {/* Mobile / Fallback Camera Input */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="user"
        className="hidden"
        onChange={(e) => {
          pick(e.target.files?.[0])
          e.target.value = ''
        }}
      />

      {/* Engine warm-up hint (only while first-time model download is in flight) */}
      {modelStatus === 'loading' && (
        <div className="flex items-center justify-center gap-2 text-[11px] text-slate-500">
          <span className="w-2.5 h-2.5 rounded-full border border-slate-600 border-t-brass animate-spin" />
          Warming up on-device analysis engine…
        </div>
      )}

      {/* Privacy Hint */}
      <div className="pt-4 border-t border-slate-900">
        <p className="text-center text-[11px] text-slate-500 leading-relaxed">
          🔒 Analysis runs entirely in your browser — your photo is never uploaded · Scores are for entertainment & educational reference only
        </p>
      </div>
    </div>
  )
}
